import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, MenuItem,
  Alert, Chip, CircularProgress, Divider
} from '@mui/material';
import { ArrowBack, Save, BeachAccess, AttachFile } from '@mui/icons-material';
import axios from 'axios';
import { uploadAttachment } from './AttachmentsPanel';
import { locale } from '../utils/currency';
import { isRealDate } from '../utils/dateValidation';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const calcWorkingDays = (start, end) => {
  if (!start || !end) return 0;
  let count = 0;
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  const e = new Date(end);   e.setHours(0, 0, 0, 0);
  if (e < d) return 0;
  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
};

const calcCalendarDays = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = new Date(end);   e.setHours(0, 0, 0, 0);
  if (e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
};

// Returns the date that is exactly `days` calendar days after dateStr (inclusive start)
const addCalendarDays = (dateStr, days) => {
  if (!dateStr || !days) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days - 1);
  return d.toISOString().split('T')[0];
};

const nextWorkingDay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const emptyForm = {
  employeeNo: '', leaveTypeCode: '', startDate: '', endDate: '',
  relieverNo: '', reason: '',
};

const LeaveApplicationForm = () => {
  const navigate = useNavigate();

  const [form,       setForm]       = useState(emptyForm);
  const [employee,   setEmployee]   = useState(null);
  const [leaveType,  setLeaveType]  = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [balance,    setBalance]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [validErr,   setValidErr]   = useState('');
  const [file,       setFile]       = useState(null); // supporting document

  // Load employees + leave types once
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/employees`, { headers: hdrs() }),
      axios.get(`${API}/api/leave/types`, { headers: hdrs() }),
    ]).then(([empRes, ltRes]) => {
      setEmployees(empRes.data || []);
      setLeaveTypes(ltRes.data || []);
    }).catch(() => setError('Failed to load data'));
  }, []);

  // When employee changes, load their details
  useEffect(() => {
    if (!form.employeeNo) { setEmployee(null); return; }
    const emp = employees.find(e => e.employeeNo === form.employeeNo);
    setEmployee(emp || null);
    // Reset leave type if gender constraint no longer applies
    setForm(f => ({ ...f, leaveTypeCode: '', startDate: '', endDate: '' }));
    setLeaveType(null); setBalance(null);
  }, [form.employeeNo, employees]);

  // When leave type changes
  useEffect(() => {
    if (!form.leaveTypeCode) { setLeaveType(null); setBalance(null); return; }
    const lt = leaveTypes.find(t => t.code === form.leaveTypeCode);
    setLeaveType(lt || null);
  }, [form.leaveTypeCode, leaveTypes]);

  // Fetch balance when employee + leave type + year are set
  useEffect(() => {
    if (!form.employeeNo || !form.leaveTypeCode) { setBalance(null); return; }
    const year = form.startDate ? new Date(form.startDate).getFullYear() : new Date().getFullYear();
    axios.get(`${API}/api/leave/balance/${form.employeeNo}?year=${year}`, { headers: hdrs() })
      .then(r => {
        const b = (r.data || []).find(x => x.leaveTypeCode === form.leaveTypeCode);
        setBalance(b ? b.balance : 0);
      }).catch(() => setBalance(null));
  }, [form.employeeNo, form.leaveTypeCode, form.startDate]);

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };

      // Resolve the leave type that will apply after this change
      const ltCode  = name === 'leaveTypeCode' ? value : f.leaveTypeCode;
      const lt      = leaveTypes.find(t => t.code === ltCode);
      const maxDays = lt?.maxDaysPerApplication || 0;

      // Auto-fill end date for fixed-duration leave types (calendar days)
      if (name === 'startDate' && maxDays > 0 && value) {
        next.endDate = addCalendarDays(value, maxDays);
      }
      if (name === 'leaveTypeCode') {
        if (maxDays > 0 && f.startDate) {
          // New type is fixed-duration — auto-fill from existing start date
          next.endDate = addCalendarDays(f.startDate, maxDays);
        } else if (maxDays === 0) {
          // Switching to a flexible type — clear any auto-filled end date
          const prevLt = leaveTypes.find(t => t.code === f.leaveTypeCode);
          if ((prevLt?.maxDaysPerApplication || 0) > 0) next.endDate = '';
        }
      }

      // Return date is always the next working day after end date
      if (next.endDate) next.returnDate = nextWorkingDay(next.endDate);

      return next;
    });
    setValidErr('');
  };

  // Fixed-duration types (Paternity, Maternity, Compassionate…) count all calendar days
  const isFixedDuration = !!(leaveType && leaveType.maxDaysPerApplication > 0);
  const leaveDays  = isFixedDuration
    ? calcCalendarDays(form.startDate, form.endDate)
    : calcWorkingDays(form.startDate, form.endDate);
  const returnDate = nextWorkingDay(form.endDate);

  // Validate client-side before submitting
  const validate = () => {
    if (!form.employeeNo)    return 'Please select an employee';
    if (!form.leaveTypeCode) return 'Please select a leave type';
    if (!form.startDate)     return 'Start date is required';
    if (!form.endDate)       return 'End date is required';
    if (new Date(form.endDate) < new Date(form.startDate)) return 'End date cannot be before start date';
    if (leaveDays <= 0)      return isFixedDuration ? 'No days in the selected range' : 'No working days in the selected range';

    if (leaveType) {
      // Gender check
      if (leaveType.gender !== 'All' && employee?.gender !== leaveType.gender) {
        return `${leaveType.name} is only available for ${leaveType.gender} employees`;
      }
      // Max days per application
      if (leaveType.maxDaysPerApplication > 0 && leaveDays > leaveType.maxDaysPerApplication) {
        return `${leaveType.name} cannot exceed ${leaveType.maxDaysPerApplication} days per application (requested: ${leaveDays})`;
      }
      // Balance check for limited types
      if (leaveType.daysPerYear !== null && balance !== null && balance < leaveDays) {
        return `Insufficient balance — available: ${balance} day${balance !== 1 ? 's' : ''}, requested: ${leaveDays} days`;
      }
      // Reliever required
      if (leaveType.requiresReliever && !form.relieverNo) {
        return `${leaveType.name} requires a reliever — please assign one`;
      }
    }
    return '';
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setValidErr(err); return; }
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/api/leave/applications`, {
        employeeNo:    form.employeeNo,
        leaveTypeCode: form.leaveTypeCode,
        startDate:     form.startDate,
        endDate:       form.endDate,
        relieverNo:    form.relieverNo || undefined,
        reason:        form.reason,
      }, { headers: hdrs() });
      // Supporting document (sick sheet, invitation letter…) hangs off the application
      if (file && r.data?.applicationNo) {
        try { await uploadAttachment(file, 'LeaveApplication', r.data.applicationNo); }
        catch { /* application submitted; attachment can be re-added from the list */ }
      }
      navigate('/hr/leave/applications');
    } catch (err) { setError(err.response?.data?.error || 'Submit failed'); }
    finally { setSaving(false); }
  };

  // Filter leave types by employee gender
  const eligibleTypes = employee
    ? leaveTypes.filter(lt => lt.gender === 'All' || lt.gender === employee.gender)
    : leaveTypes;

  const balanceColor = balance === null ? 'text.secondary'
    : balance <= 0 ? 'error.main'
    : balance <= 5  ? 'warning.main'
    : 'success.main';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, mb: 6 }}>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/hr/leave/applications')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Box display="flex" alignItems="center" gap={1}>
                <BeachAccess />
                <Typography variant="h6" fontWeight={700}>New Leave Application</Typography>
              </Box>
            </Box>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave} disabled={saving}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              {saving ? 'Submitting…' : 'Submit Application'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error    && <Alert severity="error"   sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {validErr && <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setValidErr('')}>{validErr}</Alert>}

      <Card elevation={3}>
        <CardContent>
          <Grid container spacing={2}>

            {/* Employee */}
            <Grid item xs={12}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                Employee
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Employee *" name="employeeNo" value={form.employeeNo}
                onChange={handle} fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}>
                <MenuItem value=""><em>— Select employee —</em></MenuItem>
                {employees.map(e => (
                  <MenuItem key={e.employeeNo} value={e.employeeNo}>
                    {e.firstName} {e.lastName} <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({e.employeeNo})</span>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {employee && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={700}>{employee.firstName} {employee.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {employee.jobTitle || '—'} · {employee.department || '—'} · Gender: {employee.gender || 'Not set'}
                  </Typography>
                  {employee.employmentDate && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Employed: {new Date(employee.employmentDate).toLocaleDateString(locale())}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Leave Type */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                Leave Details
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Leave Type *" name="leaveTypeCode" value={form.leaveTypeCode}
                onChange={handle} fullWidth size="small" disabled={!form.employeeNo}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                <MenuItem value=""><em>— Select leave type —</em></MenuItem>
                {eligibleTypes.map(lt => (
                  <MenuItem key={lt.code} value={lt.code}>
                    {lt.name}
                    {lt.daysPerYear !== null
                      ? <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({lt.daysPerYear} days/yr)</span>
                      : <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>(unlimited)</span>
                    }
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Balance indicator */}
            {leaveType && form.employeeNo && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {leaveType.isPaid !== false ? '✓ Paid leave' : '✗ Unpaid leave'} ·
                    {leaveType.carryForward ? ` Carry forward (max ${leaveType.maxCarryForwardDays || '∞'} days)` : ' No carry forward'}
                    {leaveType.requiresReliever ? ' · Reliever required' : ''}
                  </Typography>
                  {leaveType.daysPerYear !== null && (
                    <Typography variant="body2" mt={0.5}>
                      Balance:{' '}
                      <strong style={{ color: balanceColor === 'text.secondary' ? '#555' : undefined }}>
                        {balance === null ? '…' : `${balance} days`}
                      </strong>
                      {leaveDays > 0 && balance !== null && (
                        <span style={{ color: balance >= leaveDays ? '#2e7d32' : '#c62828', fontSize: 12, marginLeft: 8 }}>
                          {balance >= leaveDays ? `✓ Sufficient (${leaveDays} requested)` : `✗ Insufficient (${leaveDays} requested)`}
                        </span>
                      )}
                    </Typography>
                  )}
                  {leaveType.maxDaysPerApplication > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Max {leaveType.maxDaysPerApplication} days per application
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Dates */}
            <Grid item xs={12} sm={4}>
              <TextField label="Start Date *" name="startDate" type="date" value={form.startDate}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                disabled={!form.leaveTypeCode}
                error={!!(form.startDate && !isRealDate(form.startDate))}
                helperText={form.startDate && !isRealDate(form.startDate) ? 'Not a valid date' : ''} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="End Date *" name="endDate" type="date" value={form.endDate}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                disabled={!form.startDate}
                inputProps={{ min: form.startDate }}
                error={!!(form.endDate && !isRealDate(form.endDate))}
                helperText={(form.endDate && !isRealDate(form.endDate)) ? 'Not a valid date' : isFixedDuration && form.endDate
                  ? `Auto-calculated · ${leaveType.maxDaysPerApplication} calendar days`
                  : ''}
                sx={isFixedDuration && form.endDate ? { '& .MuiInputBase-input': { color: '#1565c0' } } : {}} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {isFixedDuration ? 'Calendar Days' : 'Working Days'}
                </Typography>
                <Typography variant="h5" fontWeight={800} color={leaveDays > 0 ? '#1565c0' : 'text.disabled'} lineHeight={1.2}>
                  {leaveDays > 0 ? leaveDays : '—'}
                </Typography>
                {returnDate && leaveDays > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Return: {new Date(returnDate).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Optional fields */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                Additional Details
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select
                label={leaveType?.requiresReliever ? 'Reliever *' : 'Reliever (Optional)'}
                name="relieverNo" value={form.relieverNo}
                onChange={handle} fullWidth size="small"
                error={!!(leaveType?.requiresReliever && !form.relieverNo && validErr)}
                helperText={leaveType?.requiresReliever
                  ? (form.relieverNo ? 'Reliever assigned' : 'Required — this leave type mandates a reliever')
                  : ''}
                FormHelperTextProps={{ sx: { color: leaveType?.requiresReliever && !form.relieverNo ? 'error.main' : 'success.main' } }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {employees.filter(e => e.employeeNo !== form.employeeNo).map(e => (
                  <MenuItem key={e.employeeNo} value={e.employeeNo}>
                    {e.firstName} {e.lastName} ({e.employeeNo})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Reason / Remarks" name="reason" value={form.reason}
                onChange={handle} fullWidth size="small" multiline rows={2}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Button component="label" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none' }}>
                  Attach supporting document
                  <input type="file" hidden onChange={e => setFile(e.target.files?.[0] || null)} />
                </Button>
                {file && <Chip label={file.name} size="small" onDelete={() => setFile(null)} />}
              </Box>
            </Grid>

          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeaveApplicationForm;
